// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import styled from 'styled-components';
import React, { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setZoomLevel } from 'store/reducers/projectToolSlice';
import IconMagnifyingGlassMinus from 'components/treactui-template/icons/IconMagnifyingGlassMinus';
import IconMagnifyingGlassPlus from 'components/treactui-template/icons/IconMagnifyingGlassPlus';
import IconScannerViewMaximize from 'components/treactui-template/icons/IconScannerViewMaximize';
import { Button, Colors, DropdownHeader } from '@tls/treact-ui';

const STEP_SIZE = 0.25;
export default function CanvasZoom() {
  const dispatch = useAppDispatch();
  const zoomLevel = useAppSelector(s => s.tools.zoomLevel);

  const handleZoomChange = useCallback(
    e => {
      if (!e?.detail?.scale) return;
      dispatch(setZoomLevel(e.detail.scale));
    },
    [dispatch]
  );

  useEffect(() => {
    document.addEventListener('scalechanged', handleZoomChange);

    return () => {
      document.removeEventListener('scalechanged', handleZoomChange);
    };
  }, [handleZoomChange]);

  const resetZoom = useCallback(() => {
    document.dispatchEvent(new CustomEvent('reset', { bubbles: true }));
  }, []);

  const maximize = () => {
    document.dispatchEvent(new CustomEvent('zoomchanged', { bubbles: true, detail: { scale: zoomLevel + STEP_SIZE } }));
  };

  const setZoom = (option: { id: number }) => {
    document.dispatchEvent(new CustomEvent('zoomchanged', { bubbles: true, detail: { scale: option.id / 100 } }));
  };

  const minimize = () => {
    document.dispatchEvent(
      new CustomEvent('zoomchanged', { bubbles: true, detail: { scale: Math.max(0.01, zoomLevel - STEP_SIZE) } })
    );
  };

  return (
    <BottomRight>
      <Button
        variant='transparent'
        icon={<IconMagnifyingGlassMinus />}
        noShadow
        noBorder
        onClick={minimize}
        data-testid='canvas-zoom-out-button'
      />
      <DropdownHeader
        className='dropdown'
        label=''
        noBorder
        selectedOption={{ id: zoomLevel * 100 }}
        keySelector={e => e.id}
        onChange={setZoom}
        labelSelector={e => `${e.id.toFixed(0)} %`}
        options={[{ id: 100 }, { id: 150 }, { id: 200 }, { id: 250 }, { id: 300 }]}
        data-testid='canvas-zoom-dropdown'
        dataTestIdSelector={option => `canvas-zoom-option-${option.id}`}
      />
      <Button
        variant='transparent'
        icon={<IconMagnifyingGlassPlus />}
        noShadow
        noBorder
        onClick={maximize}
        data-testid='canvas-zoom-in-button'
      />
      <Button
        variant='transparent'
        icon={<IconScannerViewMaximize />}
        noShadow
        noBorder
        onClick={resetZoom}
        data-testid='canvas-zoom-reset-button'
      />
    </BottomRight>
  );
}

const BottomRight = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: rgba(${Colors.trwhite.rgb}, 0.8);
  padding: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .dropdown {
    height: 2.5rem;
    background-color: transparent;
  }
`;
